import type { FinancialPerformanceIndicatorsResponse, TreeFPIContribution } from 'venn-api';
import { useMemo, useContext } from 'react';
import { type FPITreeRow } from '../../../../studio-blocks/types';
import type { ColDef, ColGroupDef } from 'ag-grid-community';
import { getComparisonLabelForBlockLegend, useHasFF } from 'venn-utils';
import HeaderCellRenderer from '../../../../studio-blocks/components/grid/renderers/HeaderCellRenderer';
import { get } from 'lodash';
import ValueCellRenderer from '../../../../studio-blocks/components/grid/renderers/ValueCellRenderer';
import {
  getDefaultCellClass,
  getCurrencyCellClass,
  getDefaultValueFormat,
} from '../../../../studio-blocks/logic/gridStyling';
import { useRecoilValue } from 'recoil';
import { blockLimitedHoldingsSubjectsWithFees } from 'venn-state';
import { useSelectedMetrics } from '../../../../studio-blocks/logic/useSelectedMetrics';
import { usePortfolioMultiFiltering } from '../../../utils/hooks/use-portfolio-multi-filtering';
import { UserContext } from '../../../../contexts';
import { isCurrencyData } from './useFinancialPerformanceIndicatorsTable';

type RowMap = { [path: string]: FPITreeRow };

// This contains null byte so should be safe to use as separator
const SEPARATOR = '#\u0000#';

const fillTreeData = (
  contributions: TreeFPIContribution,
  currentPath: string[],
  rowMap: RowMap,
  subjectIndex: number,
  isRoot: boolean,
  hasMultiLevelPortfolioFiltering: boolean,
) => {
  const node = contributions.value;
  const name = isRoot && currentPath.length === 0 ? 'Total' : node.name;
  const path = [...currentPath, name];
  const pathKey = path.join(SEPARATOR);

  if (!rowMap[pathKey]) {
    rowMap[pathKey] = {
      key: pathKey,
      label: name,
      path,
      value: [],
      isStrategy: node.id.type !== 'fund',
      fundId: !isRoot && node.id.type === 'fund' ? (node.id as { id: string }).id : undefined,
    };
  }

  rowMap[pathKey].value[subjectIndex] = node.metadata.financialPerformanceIndicators;

  contributions.children.forEach((child) =>
    fillTreeData(
      child,
      hasMultiLevelPortfolioFiltering && isRoot ? [] : path,
      rowMap,
      subjectIndex,
      false,
      hasMultiLevelPortfolioFiltering,
    ),
  );
};

const getTreeData = (
  data: FinancialPerformanceIndicatorsResponse,
  hasMultiLevelPortfolioFiltering: boolean,
): FPITreeRow[] => {
  const rowMap = {};
  data.contributions.forEach((contribution, index) =>
    fillTreeData(contribution, [], rowMap, index, true, hasMultiLevelPortfolioFiltering),
  );
  return Object.values(rowMap);
};

export const useFinancialPerformanceIndicatorsTreeTable = (
  id: string,
  data: FinancialPerformanceIndicatorsResponse,
) => {
  const { hasMultiLevelPortfolioFiltering } = usePortfolioMultiFiltering(id);
  const allSubjects = useRecoilValue(blockLimitedHoldingsSubjectsWithFees(id));
  const selectedMetrics = useSelectedMetrics();
  const userContext = useContext(UserContext);
  const workspaceCurrency = userContext.profileSettings?.organization?.currency;
  const skipKSuffixInFormatting = useHasFF('skip_k_suffix_in_formatting_ff');

  const rowData = useMemo(
    (): FPITreeRow[] => getTreeData(data, !!hasMultiLevelPortfolioFiltering),
    [data, hasMultiLevelPortfolioFiltering],
  );

  const columnDefs = useMemo(
    (): ColGroupDef<FPITreeRow>[] =>
      allSubjects.map((subject, subjectIndex) => ({
        marryChildren: true,
        headerName: getComparisonLabelForBlockLegend(subject),
        headerGroupComponent: HeaderCellRenderer,
        headerGroupComponentParams: {
          subject,
        },
        children: selectedMetrics.map((metric): ColDef<FPITreeRow> => {
          const isCurrency = isCurrencyData(metric.key, selectedMetrics);
          return {
            headerName: metric.label,
            valueGetter: ({ data }) => get(data?.value[subjectIndex], metric.metricPath!),
            valueFormatter: ({ value, colDef }) =>
              getDefaultValueFormat({
                value,
                data: { type: metric.dataType },
                colDef,
                currency: isCurrency ? workspaceCurrency : undefined,
              }),
            cellRenderer: ValueCellRenderer,
            cellClass: isCurrency ? getCurrencyCellClass(workspaceCurrency) : getDefaultCellClass,
            autoHeight: false,
            cellRendererParams: {
              skipKSuffixInFormatting,
            },
          };
        }),
      })),
    [allSubjects, selectedMetrics, workspaceCurrency, skipKSuffixInFormatting],
  );

  return { rowData, columnDefs };
};
